import React, {useMemo, useState, useEffect} from 'react';
import {Alert, Box, DialogContentText, Divider, InputAdornment, MenuItem, Select, Stack, Switch, Tooltip, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoHover, {HOVER_BOX} from '../../../shared-components/InfoHover';
import {validateEmail, validateFieldLength, validateMultipleEmails} from '../../../utility/validationUtil';
import AdmicityForm from '../../../shared-components/AdmicityForm';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import AdmicityCronScheduler from '../../../shared-components/AdmicityCronScheduler';
import LogoUploader from './LogoUploader';
import useUser from '../../../utility/hooks/useUser';
import {ROLES} from '../../../constants/roles';
import StudentParentInvitationsSettings from './StudentParentInvitationsSettings';
import {PARENT_INVITATION_TARGET_MODES} from '../../../constants/parentInvitationTargetModes';
import {FOUR_REMINDERS_TO_A_ROW, REMINDER_SPAN} from './reminderLayout';
import SchoolAddressField, {useSchoolAddress} from './SchoolAddressField';

const validateDFENumber = (value) => {
    const isValid = /^\d{3}\d{4}$/.test(value);
    return {
        isValid,
        message: isValid ? '' : 'DFE number must be in the format: 3 digits for authority and 4 digits for school number',
    };
};

const validateInterval = (value) => {
    const numericValue = +value;
    const isValid = Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 1000;

    return {
        isValid,
        message: isValid ? '' : 'The value must be a whole number between 0 and 1000. Enter 0 to disable.',
    };
};

const validateWebDomain = (value) => {
    const isValid = /^([a-zA-Z0-9][a-zA-Z0-9-_]*\.)*[a-zA-Z0-9]*[a-zA-Z0-9-_]*[a-zA-Z0-9]+$/.test(value) &&
        !value.includes('admicity.co.uk');

    return {
        isValid,
        message: isValid ? '' : 'Enter a valid domain (e.g., hogwarts) without ".admicity.co.uk"',
    };
};

// UpdateSchoolCommandHandler stores at least 1, so 0 cannot switch the warning off
const validateWarningDays = (value) => {
    const numericValue = +value;
    const isValid = Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 365;

    return {
        isValid,
        message: isValid ? '' : 'The value must be a whole number between 1 and 365.',
    };
};

const normaliseIds = (ids) => (ids ?? [])
    .filter(id => id > 0)
    .slice()
    .sort((left, right) => left - right);

const areNumberArraysEqual = (left, right) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);

// Maria, 20 October 2026: each section is a white card with its heading inside it, and the cards sit in two
// columns, so the page needs far less scrolling
const CARD_SX = {
    backgroundColor: '#fff',
    border: '1px solid #e3e8ef',
    borderRadius: 2,
    px: 2,
    py: 1.25,
    mb: 1.5,
    breakInside: 'avoid'
};
// Maria, 20 October 2026: every heading the same as the Auto Reminders heading, and the setting names in normal weight
// the gap under a heading is the same as the gap between two settings: 4px + the line + 4px between rows, and each
// row's text sits 3px inside its 30px height, so 15px from text to text (Maria, 20 October 2026: less padding)
const CARD_TITLE_SX = {fontWeight: 600, mb: '12px'};
// every row in a card is the same height, whether it has a switch, a button or a number box
const ROW_MIN_HEIGHT = 30;
const settingsSectionSx = {};
const NUMBER_INPUT_SX = {
    width: 72,
    px: 0.75,
    py: 0.25,
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    flexShrink: 0
};

// Maria, 20 October 2026: hovering over a switch says plainly what the setting is now. Each is given whether the
// switch is on (blue).
const ENABLED = on => (on ? 'ENABLED' : 'DISABLED');
const SWITCH_STATE = {
    disableEnquiriesSync: on => `Enquiries Synchronization is currently ${ENABLED(!on)}`,
    hideBoarderGroups: on => `Boarder Groups are currently ${on ? 'DISABLED and WILL NOT APPEAR' : 'ENABLED and WILL APPEAR'} in filters`,
    hideHouseGroups: on => `House Groups are currently ${on ? 'DISABLED and WILL NOT APPEAR' : 'ENABLED and WILL APPEAR'} in filters`,
    disableApplicationParentInvitations: on => `Applicant Parent Invitations are currently ${ENABLED(!on)}`,
    disableStudentInvitations: on => `Student Invitations are currently ${ENABLED(!on)}`,
    disableEmployeeInvitations: on => `Employee Invitations are currently ${ENABLED(!on)}`,
    disableParentNotificationApplicationUpdates: on => `Parent Notifications for Application Profile Updates are currently ${ENABLED(!on)}`,
    disableParentNotificationStudentUpdates: on => `Parent Notifications for Student Profile Updates are currently ${ENABLED(!on)}`,
    mfaEnabled: on => `Email verification codes on login are currently ${ENABLED(on)}`
};

const getItemChecked = (item, value) => {
    const rawValue = value?.[item.name] ?? false;
    return item.invertChecked ? !rawValue : rawValue;
};

const SettingsToggleRow = ({item, checked, onChange}) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{minHeight: ROW_MIN_HEIGHT}}>
        <Box>
            <Typography>{item.label}</Typography>
            {item.description && (
                <Typography variant="body2" color="text.secondary">
                    {item.description}
                </Typography>
            )}
        </Box>
        <Tooltip
            title={SWITCH_STATE[item.name]?.(item.invertChecked ? !checked : checked) ?? ''}
            arrow
            placement="left"
            componentsProps={HOVER_BOX}
        >
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}} data-testid={`switch-${item.name}`}>
                <Switch
                    size="small"
                    checked={checked}
                    onChange={(event) => onChange(event.target.checked)}
                />
            </Box>
        </Tooltip>
    </Stack>
);

// AD-2646: whether applicants' parents who choose "currently in receipt" of Free School Meals are asked for their date
// of birth and NI number. Off until the school chooses.
export const FSM_IN_RECEIPT_CHOICES = [
    {value: 0, label: 'No'},
    {value: 1, label: 'Optional'},
    {value: 2, label: 'Required'}
];

const SettingsToggleSection = ({title, hover, items, value, onChange}) => (
    <Box sx={CARD_SX}>
        {title && (
            <Typography sx={{...CARD_TITLE_SX, display: 'flex', alignItems: 'center', gap: 0.5}}>
                {title}
                {hover && <InfoHover text={hover} testId={`hover-${title}`}/>}
            </Typography>
        )}
        <Box sx={settingsSectionSx}>
            <Stack
                spacing={0.5}
                divider={<Divider flexItem />}
            >
                {items.map(item => (
                    <Box key={item.name}>
                        <SettingsToggleRow
                            item={item}
                            checked={getItemChecked(item, value)}
                            onChange={(nextChecked) => onChange({
                                ...value,
                                [item.name]: item.invertChecked ? !nextChecked : nextChecked
                            })}
                        />
                    </Box>
                ))}
            </Stack>
        </Box>
    </Box>
);

SettingsToggleRow.propTypes = {
    item: PropTypes.shape({
        label: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        invertChecked: PropTypes.bool
    }).isRequired,
    checked: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

SettingsToggleSection.propTypes = {
    title: PropTypes.string,
    hover: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        invertChecked: PropTypes.bool
    })).isRequired,
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired
};

const SchoolSetupForm = (
    {
        editMode,
        schoolInfo,
        onSubmit,
        isLoading,
        logo
    }) => {
    const [showAlert, setShowAlert] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const [cronExpression, setCronExpression] = useState('');
    const [photoCronExpression, setPhotoCronExpression] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);
    const [pendingShowAlert, setPendingShowAlert] = useState(false);
    const [pendingInvitationWarning, setPendingInvitationWarning] = useState(false);
    const [pendingEnabledInvitationLabels, setPendingEnabledInvitationLabels] = useState([]);
    const {user} = useUser();

    const getParentInvitationSettings = (source) => ({
        disableParentInvitations: source.disableParentInvitations ?? true,
        parentInvitationTargetMode: source.parentInvitationTargetMode ??
            PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
        parentInvitationYearGroupIds: source.parentInvitationYearGroupIds ?? []
    });
    const getGroupSettings = (source) => ({
        disableEnquiriesSync: source.disableEnquiriesSync ?? false,
        hideBoarderGroups: source.hideBoarderGroups ?? false,
        hideHouseGroups: source.hideHouseGroups ?? false
    });
    const getInvitationSettings = (source) => ({
        disableApplicationParentInvitations: source.disableApplicationParentInvitations ?? false,
        disableStudentInvitations: source.disableStudentInvitations ?? false,
        disableEmployeeInvitations: source.disableEmployeeInvitations ?? false
    });
    const getNotificationSettings = (source) => ({
        disableParentNotificationApplicationUpdates: source.disableParentNotificationApplicationUpdates ?? false,
        disableParentNotificationStudentUpdates: source.disableParentNotificationStudentUpdates ?? false
    });
    const getMfaSettings = (source) => ({
        mfaEnabled: source.mfaEnabled ?? false,
        mfaDeviceTrustDays: source.mfaDeviceTrustDays ?? 30
    });
    const [parentInvitationSettings, setParentInvitationSettings] = useState(getParentInvitationSettings(schoolInfo));
    const [groupSettings, setGroupSettings] = useState(getGroupSettings(schoolInfo));
    const [invitationSettings, setInvitationSettings] = useState(getInvitationSettings(schoolInfo));
    const [notificationSettings, setNotificationSettings] = useState(getNotificationSettings(schoolInfo));
    const [mfaSettings, setMfaSettings] = useState(getMfaSettings(schoolInfo));
    const [fsmInReceiptContacts, setFsmInReceiptContacts] = useState(schoolInfo.fsmInReceiptContacts ?? 0);
    // AD-2228: the school's address, which applicants' distances from the school are measured from
    const [schoolAddress, setSchoolAddress] = useSchoolAddress(schoolInfo.address);
    const [currentLogo, setCurrentLogo] = useState(logo);

    useEffect(() => {
        setParentInvitationSettings(getParentInvitationSettings(schoolInfo));
    }, [
        schoolInfo.disableParentInvitations,
        schoolInfo.parentInvitationTargetMode,
        schoolInfo.parentInvitationYearGroupIds
    ]);

    useEffect(() => {
        setCurrentLogo(logo);
    }, [logo]);

    useEffect(() => {
        setGroupSettings(getGroupSettings(schoolInfo));
        setInvitationSettings(getInvitationSettings(schoolInfo));
        setNotificationSettings(getNotificationSettings(schoolInfo));
        setMfaSettings(getMfaSettings(schoolInfo));
    }, [
        schoolInfo.disableEnquiriesSync,
        schoolInfo.hideBoarderGroups,
        schoolInfo.hideHouseGroups,
        schoolInfo.disableApplicationParentInvitations,
        schoolInfo.disableStudentInvitations,
        schoolInfo.disableEmployeeInvitations,
        schoolInfo.disableParentNotificationApplicationUpdates,
        schoolInfo.disableParentNotificationStudentUpdates,
        schoolInfo.mfaEnabled,
        schoolInfo.mfaDeviceTrustDays
    ]);

    useEffect(() => {
        const validateForm = () => {
            const fieldsToValidate = [
                { name: 'name', value: schoolInfo.name || '', required: true, validate: (value) => validateFieldLength(value, 400) },
                { name: 'dfeNumber', value: schoolInfo.dfeNumber || '', required: true, validate: validateDFENumber },
                { name: 'webDomain', value: schoolInfo.domain || '', required: true, validate: validateWebDomain },
                { name: 'emailAddresses', value: schoolInfo.emailAddresses || '', required: false, validate: validateMultipleEmails },
                { name: 'notes', value: schoolInfo.notes || '', required: false, validate: () => ({ isValid: true }) }
            ];

            const hasErrors = fieldsToValidate.some(field => {
                if (!field.required && (!field.value || field.value === '')) {
                    return false;
                }
                const validation = field.validate(field.value);
                return !validation.isValid;
            });

            setIsFormValid(!hasErrors);
        };

        validateForm();
    }, [schoolInfo]);

    const shouldWarnAboutInvitationSend = (body) => {
        const currentParentSettings = getParentInvitationSettings(schoolInfo);
        const nextParentSettings = {
            disableParentInvitations: body.disableParentInvitations,
            parentInvitationTargetMode: body.parentInvitationTargetMode,
            parentInvitationYearGroupIds: normaliseIds(body.parentInvitationYearGroupIds)
        };

        const currentParentYearGroupIds = normaliseIds(currentParentSettings.parentInvitationYearGroupIds);
        const isParentInvitationTargetingChanged =
            currentParentSettings.parentInvitationTargetMode !== nextParentSettings.parentInvitationTargetMode ||
            !areNumberArraysEqual(currentParentYearGroupIds, nextParentSettings.parentInvitationYearGroupIds);

        const willSendParentInvitations =
            (schoolInfo.disableParentInvitations && !body.disableParentInvitations) ||
            (isParentInvitationTargetingChanged && !body.disableParentInvitations);

        return willSendParentInvitations ||
            (schoolInfo.disableApplicationParentInvitations && !body.disableApplicationParentInvitations) ||
            (schoolInfo.disableStudentInvitations && !body.disableStudentInvitations) ||
            (schoolInfo.disableEmployeeInvitations && !body.disableEmployeeInvitations);
    };

    const getEnabledInvitationLabels = (body) => {
        const labels = [];
        const currentParentSettings = getParentInvitationSettings(schoolInfo);
        const nextParentSettings = {
            disableParentInvitations: body.disableParentInvitations,
            parentInvitationTargetMode: body.parentInvitationTargetMode,
            parentInvitationYearGroupIds: normaliseIds(body.parentInvitationYearGroupIds)
        };
        const currentParentYearGroupIds = normaliseIds(currentParentSettings.parentInvitationYearGroupIds);
        const isParentInvitationTargetingChanged =
            currentParentSettings.parentInvitationTargetMode !== nextParentSettings.parentInvitationTargetMode ||
            !areNumberArraysEqual(currentParentYearGroupIds, nextParentSettings.parentInvitationYearGroupIds);

        if (schoolInfo.disableApplicationParentInvitations && !body.disableApplicationParentInvitations) {
            labels.push('Applicant\'s Parent Invitations');
        }

        if (
            (schoolInfo.disableParentInvitations && !body.disableParentInvitations) ||
            (isParentInvitationTargetingChanged && !body.disableParentInvitations)
        ) {
            labels.push('Student\'s Parent Invitations');
        }

        if (schoolInfo.disableStudentInvitations && !body.disableStudentInvitations) {
            labels.push('Student Invitations');
        }

        if (schoolInfo.disableEmployeeInvitations && !body.disableEmployeeInvitations) {
            labels.push('Employee Invitations');
        }

        return labels;
    };

    const handleSubmit = (payload) => {
        const nextParentInvitationSettings = payload.parentInvitationSettings ??
            parentInvitationSettings;
        const nextGroupSettings = payload.groupSettings ?? getGroupSettings(schoolInfo);
        const nextInvitationSettings = payload.invitationSettings ?? getInvitationSettings(schoolInfo);
        const nextNotificationSettings = payload.notificationSettings ?? getNotificationSettings(schoolInfo);
        const nextMfaSettings = payload.mfaSettings ?? getMfaSettings(schoolInfo);
        const body =
            !editMode
                ? {
                    name: payload.name,
                    dfeNumber: payload.dfeNumber,
                    subDomain: payload.domain,
                    domain: process.env.REACT_APP_DOMAIN,
                    notes: payload.notes,
                    registrationInviteConfigOption: payload.registrationInviteConfigOption,
                    emailAddresses: payload.emailAddresses,
                    noAccountAutoReminderInterval: payload.noAccountAutoReminderInterval,
                    unconfirmedApplicationReminderIntervalInHours: payload.unconfirmedApplicationReminderIntervalInHours,
                    unconfirmedStudentReminderIntervalInHours: payload.unconfirmedStudentReminderIntervalInHours,
                    syncInterval: cronExpression,
                    syncPhotoInterval: photoCronExpression,
                    hideBoarderGroups: nextGroupSettings.hideBoarderGroups,
                    hideHouseGroups: nextGroupSettings.hideHouseGroups,
                    disableParentInvitations: nextParentInvitationSettings.disableParentInvitations,
                    parentInvitationTargetMode: nextParentInvitationSettings.parentInvitationTargetMode,
                    parentInvitationYearGroupIds: nextParentInvitationSettings.parentInvitationYearGroupIds,
                    disableApplicationParentInvitations: nextInvitationSettings.disableApplicationParentInvitations,
                    disableStudentInvitations: nextInvitationSettings.disableStudentInvitations,
                    disableEmployeeInvitations: nextInvitationSettings.disableEmployeeInvitations,
                    disableParentNotificationApplicationUpdates: nextNotificationSettings.disableParentNotificationApplicationUpdates,
                    disableParentNotificationStudentUpdates: nextNotificationSettings.disableParentNotificationStudentUpdates,
                    disableEnquiriesSync: nextGroupSettings.disableEnquiriesSync,
                    mfaEnabled: nextMfaSettings.mfaEnabled,
                    mfaDeviceTrustDays: Number(nextMfaSettings.mfaDeviceTrustDays) || 0,
                    medicineExpiryWarningDays: Number(payload.medicineExpiryWarningDays) || 28,
                    fsmInReceiptContacts: Number(payload.fsmSettings ?? fsmInReceiptContacts) || 0,
                    logo: payload.logo,
                    enquiryResponseEmail: payload.enquiryResponseEmail,
                    address: schoolAddress
                        ? {
                            apartment: schoolAddress.apartment,
                            houseName: schoolAddress.houseName,
                            houseNumber: schoolAddress.houseNumber,
                            street: schoolAddress.street,
                            district: schoolAddress.district,
                            town: schoolAddress.town,
                            county: schoolAddress.county,
                            postcode: schoolAddress.postcode,
                            udprn: schoolAddress.udprn
                        }
                        : null
                }
                : {
                    id: schoolInfo.id,
                    name: payload.name,
                    dfeNumber: payload.dfeNumber,
                    notes: payload.notes,
                    registrationInviteConfigOption: payload.registrationInviteConfigOption,
                    emailAddresses: payload.emailAddresses,
                    noAccountAutoReminderInterval: payload.noAccountAutoReminderInterval,
                    unconfirmedApplicationReminderIntervalInHours: payload.unconfirmedApplicationReminderIntervalInHours,
                    unconfirmedStudentReminderIntervalInHours: payload.unconfirmedStudentReminderIntervalInHours,
                    syncInterval: cronExpression,
                    syncPhotoInterval: photoCronExpression,
                    hideBoarderGroups: nextGroupSettings.hideBoarderGroups,
                    hideHouseGroups: nextGroupSettings.hideHouseGroups,
                    disableParentInvitations: nextParentInvitationSettings.disableParentInvitations,
                    parentInvitationTargetMode: nextParentInvitationSettings.parentInvitationTargetMode,
                    parentInvitationYearGroupIds: nextParentInvitationSettings.parentInvitationYearGroupIds,
                    disableApplicationParentInvitations: nextInvitationSettings.disableApplicationParentInvitations,
                    disableStudentInvitations: nextInvitationSettings.disableStudentInvitations,
                    disableEmployeeInvitations: nextInvitationSettings.disableEmployeeInvitations,
                    disableParentNotificationApplicationUpdates: nextNotificationSettings.disableParentNotificationApplicationUpdates,
                    disableParentNotificationStudentUpdates: nextNotificationSettings.disableParentNotificationStudentUpdates,
                    disableEnquiriesSync: nextGroupSettings.disableEnquiriesSync,
                    mfaEnabled: nextMfaSettings.mfaEnabled,
                    mfaDeviceTrustDays: Number(nextMfaSettings.mfaDeviceTrustDays) || 0,
                    medicineExpiryWarningDays: Number(payload.medicineExpiryWarningDays) || 28,
                    fsmInReceiptContacts: Number(payload.fsmSettings ?? fsmInReceiptContacts) || 0,
                    logo: payload.logo,
                    enquiryResponseEmail: payload.enquiryResponseEmail,
                    address: schoolAddress
                        ? {
                            apartment: schoolAddress.apartment,
                            houseName: schoolAddress.houseName,
                            houseNumber: schoolAddress.houseNumber,
                            street: schoolAddress.street,
                            district: schoolAddress.district,
                            town: schoolAddress.town,
                            county: schoolAddress.county,
                            postcode: schoolAddress.postcode,
                            udprn: schoolAddress.udprn
                        }
                        : null
                };

        setPendingShowAlert(
            schoolInfo.hideBoarderGroups !== nextGroupSettings.hideBoarderGroups ||
            schoolInfo.hideHouseGroups !== nextGroupSettings.hideHouseGroups
        );
        const shouldWarn = shouldWarnAboutInvitationSend(body);
        setPendingInvitationWarning(shouldWarn);
        setPendingEnabledInvitationLabels(shouldWarn ? getEnabledInvitationLabels(body) : []);
        setPendingSubmission(body);
        setConfirmOpen(true);
    };

    const handleConfirmSave = () => {
        if (!pendingSubmission) {
            return;
        }

        setShowAlert(pendingShowAlert);
        onSubmit(pendingSubmission);
        setConfirmOpen(false);
        setPendingSubmission(null);
        setPendingShowAlert(false);
        setPendingInvitationWarning(false);
        setPendingEnabledInvitationLabels([]);
    };

    const handleCancelSave = () => {
        setConfirmOpen(false);
        setPendingSubmission(null);
        setPendingShowAlert(false);
        setPendingInvitationWarning(false);
        setPendingEnabledInvitationLabels([]);
    };

    const textFields = useMemo(() => [
                {
                    label: 'School Name',
                    name: 'name',
                    initialValue: schoolInfo.name,
                    validate: (value) => validateFieldLength(value, 400)
                },
                {
                    label: 'DFE Number',
                    name: 'dfeNumber',
                    initialValue: schoolInfo.dfeNumber,
                    validate: validateDFENumber
                },
                {
                    label: 'School Web Domain at Admicity',
                    name: 'domain',
                    initialValue: schoolInfo.domain,
                    helperText: 'Enter a domain (e.g., hogwarts.admicity.co.uk)',
                    disabled: editMode,
                    InputProps: (!editMode
                        ? {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography>{process.env.REACT_APP_DOMAIN}</Typography>
                                </InputAdornment>
                            ),
                        }
                        : {}),
                    validate: validateWebDomain
                },
                {
                    // Maria, 20 October 2026: a shorter name for the shorter box; the hover says how to add more than one
                    label: 'Profile change notification emails',
                    name: 'emailAddresses',
                    required: false,
                    initialValue: schoolInfo.emailAddresses,
                    validate: validateMultipleEmails,
                    helperText: 'Enter comma-separated email addresses (e.g., admin@school.com, notifications@school.com)'
                },
        !schoolInfo.disableEnquiriesSync && {
                    label: 'Enquiry form notification email',
                    name: 'enquiryResponseEmail',
                    type: 'email',
                    initialValue: schoolInfo.enquiryResponseEmail,
                    required: false,
                    validate: validateEmail,
                    nullableOnEmpty: true
                },
                {
                    // Maria, 20 October 2026: short names, so they fit the small boxes
                    label: 'Account Creation',
                    name: 'noAccountAutoReminderInterval',
                    required: true,
                    initialValue: schoolInfo.noAccountAutoReminderInterval,
                    validate: validateInterval
                },
                {
                    // Maria, 20 October 2026: renamed from Unconfirmed Applications
                    label: 'Applicant Profile',
                    name: 'unconfirmedApplicationReminderIntervalInHours',
                    required: true,
                    initialValue: schoolInfo.unconfirmedApplicationReminderIntervalInHours,
                    validate: validateInterval
                },
                {
                    // Maria, 20 October 2026: renamed from Unconfirmed Students
                    label: 'Student Profile',
                    name: 'unconfirmedStudentReminderIntervalInHours',
                    required: true,
                    initialValue: schoolInfo.unconfirmedStudentReminderIntervalInHours,
                    validate: validateInterval
                },
                {
                    // Maria, 20 October 2026: the medicine expiry warning beside the other reminders
                    label: 'Medicine Expiry',
                    name: 'medicineExpiryWarningDays',
                    required: true,
                    initialValue: schoolInfo.medicineExpiryWarningDays ?? 28,
                    validate: validateWarningDays
                },
                {
                    label: 'Notes',
                    name: 'notes',
                    initialValue: schoolInfo.notes,
                    multiline: true,
                    rows: 2,
                    required: false
                }
    ].filter(Boolean), [schoolInfo, editMode]);

    const toggleSections = useMemo(() => [
        {
            name: 'groupSettings',
            initialValue: groupSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <SettingsToggleSection
                    key="groupSettings"
                    // Maria, 20 October 2026: the heading, with an 'i' showing the words from the General Settings video
                    title="Show or Hide Boarders and Houses"
                    hover="Hide Boarder Groups and Hide House Groups should be switched on if your school does not have boarders or Houses. These settings will stop Boarder or House groups from appearing in filter groups and student profiles. Hover over the toggle switch to see the current status of each setting."
                    value={value}
                    onChange={(nextValue) => {
                        setGroupSettings(nextValue);
                        onChange(nextValue);
                    }}
                    items={[
                        ...(user.hasRole(ROLES.SUPER_ADMIN)
                            ? [{
                                label: 'Disable Enquiries Synchronization',
                                name: 'disableEnquiriesSync'
                            }]
                            : []),
                        {
                            label: 'Hide Boarder Groups',
                            name: 'hideBoarderGroups'
                        },
                        {
                            label: 'Hide House Groups',
                            name: 'hideHouseGroups'
                        }
                    ]}
                />;
            }
        },
        {
            name: 'invitationSettings',
            initialValue: invitationSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <Box key="invitationSettings" sx={CARD_SX}>
                    <Typography sx={{...CARD_TITLE_SX, display: 'flex', alignItems: 'center', gap: 0.5}}>
                        Account Registration Invitations
                        {/* Maria, 20 October 2026: the words from the General Settings video script */}
                        <InfoHover
                            testId="hover-invitations"
                            text="The Account Registration Invitations control which account types will receive automated account creation invitations. All the account creation invitation settings are switched off when your school is first connected to Admicity. When you are ready to enable invitations for a particular group, move the slider to white. Once a slider has been moved from blue to white, the account creation emails are sent out immediately. They cannot be recalled by switching it back to blue. Before assigning any live forms to parents, students, or employees, make sure the relevant account creation invitations are switched on first. Hover over the toggle switch to see the current status of each setting."
                        />
                    </Typography>
                    <Box sx={settingsSectionSx}>
                        <Stack spacing={0.5} divider={<Divider flexItem />}>
                            <Box>
                                <SettingsToggleRow
                                    item={{
                                        label: 'Disable Application Parent Invitations',
                                        name: 'disableApplicationParentInvitations'
                                    }}
                                    checked={getItemChecked({
                                        name: 'disableApplicationParentInvitations'
                                    }, value)}
                                    onChange={(nextChecked) => {
                                        const nextValue = {
                                            ...value,
                                            disableApplicationParentInvitations: nextChecked
                                        };
                                        setInvitationSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                />
                            </Box>
                            {/* Maria, 20 October 2026: Student Parent Invitations under Application Parent Invitations */}
                            <Box>
                                <StudentParentInvitationsSettings
                                    schoolId={schoolInfo.id}
                                    value={parentInvitationSettings}
                                    embedded
                                    onChange={(nextValue) => {
                                        setParentInvitationSettings(nextValue);
                                    }}
                                />
                            </Box>
                            <Box>
                                <SettingsToggleRow
                                    item={{
                                        label: 'Disable Student Invitations',
                                        name: 'disableStudentInvitations'
                                    }}
                                    checked={getItemChecked({
                                        name: 'disableStudentInvitations'
                                    }, value)}
                                    onChange={(nextChecked) => {
                                        const nextValue = {
                                            ...value,
                                            disableStudentInvitations: nextChecked
                                        };
                                        setInvitationSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                />
                            </Box>
                            <Box>
                                <SettingsToggleRow
                                    item={{
                                        label: 'Disable Employee Invitations',
                                        name: 'disableEmployeeInvitations'
                                    }}
                                    checked={getItemChecked({
                                        name: 'disableEmployeeInvitations'
                                    }, value)}
                                    onChange={(nextChecked) => {
                                        const nextValue = {
                                            ...value,
                                            disableEmployeeInvitations: nextChecked
                                        };
                                        setInvitationSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Box>;
            }
        },
        {
            name: 'notificationSettings',
            initialValue: notificationSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <SettingsToggleSection
                    key="notificationSettings"
                    // Maria, 20 October 2026: renamed, with an 'i' showing the words from the General Settings video
                    title="Parent Notification Emails"
                    hover="Disable Parent Notifications for Application Profile Updates and Student Profile Updates will stop profile update emails being sent to parents of Applicants or Students when a school user has updated their child's profile information. If you are making changes to a lot of profiles at once, you can temporarily move this slider from white to blue. This will prevent parents from being sent an email for every change. You can then switch it back to white to enable the notifications again, when you've finished making the profile updates. Hover over the toggle switch to see the current status of each setting."
                    value={value}
                    onChange={(nextValue) => {
                        setNotificationSettings(nextValue);
                        onChange(nextValue);
                    }}
                    items={[
                        {
                            label: 'Disable Parent Notifications for Application Profile Updates',
                            name: 'disableParentNotificationApplicationUpdates'
                        },
                        {
                            label: 'Disable Parent Notifications for Student Profile Updates',
                            name: 'disableParentNotificationStudentUpdates'
                        }
                    ]}
                />;
            }
        },
        {
            name: 'mfaSettings',
            initialValue: mfaSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <Box key="mfaSettings" sx={CARD_SX}>
                    <Typography sx={{...CARD_TITLE_SX, display: 'flex', alignItems: 'center', gap: 0.5}}>
                        Multi-factor authentication
                        {/* Maria, 20 October 2026: the words from the General Settings video script */}
                        <InfoHover
                            testId="hover-mfa"
                            text="You can select your own settings for Multi-factor authentication. Require email verification code on login asks each user for a one-time code. This is emailed to them after they enter their password. Trusted device period sets how many days a device can be used before a new code is needed. Enter 0 to ask for a code every time. Hover over the toggle switch to see the current status of each setting."
                        />
                    </Typography>
                    <Box sx={settingsSectionSx}>
                        <Stack spacing={0.5} divider={<Divider flexItem />}>
                            <SettingsToggleRow
                                item={{
                                    label: 'Require email verification code on login',
                                    name: 'mfaEnabled'
                                }}
                                checked={getItemChecked({name: 'mfaEnabled'}, value)}
                                onChange={(nextChecked) => {
                                    const nextValue = {
                                        ...value,
                                        mfaEnabled: nextChecked
                                    };
                                    setMfaSettings(nextValue);
                                    onChange(nextValue);
                                }}
                            />
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{minHeight: ROW_MIN_HEIGHT}}>
                                <Typography>Trusted device period in days (0 asks for a code every time)</Typography>
                                <Box
                                    component="input"
                                    type="number"
                                    min={0}
                                    max={365}
                                    // eslint-disable-next-line react/prop-types
                                    value={value?.mfaDeviceTrustDays ?? 30}
                                    onChange={(event) => {
                                        const nextValue = {
                                            ...value,
                                            mfaDeviceTrustDays: event.target.value
                                        };
                                        setMfaSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                    sx={NUMBER_INPUT_SX}
                                />
                            </Stack>
                        </Stack>
                    </Box>
                </Box>;
            }
        },
        {
            name: 'fsmSettings',
            initialValue: fsmInReceiptContacts,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <Box key="fsmSettings" sx={CARD_SX}>
                    <Typography sx={{...CARD_TITLE_SX, display: 'flex', alignItems: 'center', gap: 0.5}}>
                        Free School Meals
                        <InfoHover
                            testId="hover-fsm"
                            text="Parents of applicants who tick 'currently in receipt of Free School Meals' can be asked for their date of birth and National Insurance number, so that the school can check their eligibility using the Online Free School Meals service. This only applies to applicants. The details are not synced to SIMS."
                        />
                    </Typography>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{minHeight: ROW_MIN_HEIGHT}}>
                        <Typography>{"Ask applicants' parents for their date of birth and NI number"}</Typography>
                        <Select
                            size="small"
                            value={value ?? 0}
                            onChange={(event) => {
                                setFsmInReceiptContacts(event.target.value);
                                onChange(event.target.value);
                            }}
                            inputProps={{'data-testid': 'fsm-in-receipt-contacts', 'aria-label': 'Ask applicants\' parents for their date of birth and NI number'}}
                            sx={{minWidth: 120, flexShrink: 0}}
                        >
                            {FSM_IN_RECEIPT_CHOICES.map(choice => (
                                <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </Box>;
            }
        },
    ], [groupSettings, invitationSettings, notificationSettings, mfaSettings, fsmInReceiptContacts, user, schoolInfo.id, parentInvitationSettings]);

    // AD-2228 (Maria, 22 October 2026): the school's address at the top, under School Name
    const schoolAddressField = useMemo(() => ({
        name: 'schoolAddress',
        initialValue: schoolAddress,
        render: () => <SchoolAddressField value={schoolAddress} onChange={setSchoolAddress}/>
    }), [schoolAddress]);

    const customFields = useMemo(() =>
        [
            {
                name: 'logo',
                initialValue: currentLogo,
                render: props => {
                    // eslint-disable-next-line react/prop-types
                    const {onChange, ...restProps} = props;

                    // Maria, 20 October 2026: the logo is in the top card, beside the school's name
                    return (
                        <LogoUploader
                            key="logo"
                            compact
                            {...restProps}
                            onChange={(nextValue) => {
                                setCurrentLogo(nextValue);
                                onChange(nextValue);
                            }}
                        />
                    );
                }
            }
        ], [currentLogo]);

    const syncFields = useMemo(() =>
        [
            {
                name: 'syncInterval',
                initialValue: schoolInfo.syncInterval,
                render: (props) => {
                    // eslint-disable-next-line react/prop-types
                    const { onChange } = props;
                    return (
                        // Maria, 20 October 2026: one card headed 'SIMS / Admicity Synchronisation', with Data Sync and
                        // Photo Sync as its two rows; this is its top half and the photo sync its bottom half
                        <Box
                            key="syncInterval"
                            sx={{...CARD_SX, mb: 0, pb: 0, borderBottom: 'none', borderBottomLeftRadius: 0, borderBottomRightRadius: 0, breakAfter: 'avoid'}}
                        >
                            <Typography sx={{...CARD_TITLE_SX, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                SIMS / Admicity Synchronisation
                                <InfoHover
                                    testId="hover-synchronisation"
                                    text="This is a two-way exchange of information. The most recent change is shown in both Admicity and SIMS, whichever system it was made in. Synchronising can slow down both systems while in progress, especially in schools with a large SIMS database, so you can choose to run your syncs outside the school day. Hover over the toggle switch to see the current status of each setting."
                                />
                            </Typography>
                            <Box sx={settingsSectionSx}>
                                <AdmicityCronScheduler
                                    value={schoolInfo.syncInterval}
                                    onChange={(cron) => {
                                        setCronExpression(cron);
                                        if (onChange) {
                                            onChange(cron);
                                        }
                                    }}
                                    label="Sync"
                                    // Maria, 20 October 2026: the name with an 'i', in the words of the General Settings video script
                                    title="Data Sync"
                                    hover="Use the slider to turn the data sync on, then choose the days and times it runs."
                                />
                            </Box>
                        </Box>
                    );
                }
            },
            {
                name: 'syncPhotoInterval',
                initialValue: schoolInfo.syncPhotoInterval,
                render: (props) => {
                    // eslint-disable-next-line react/prop-types
                    const { onChange } = props;
                    return (
                        <Box
                            key="syncPhotoInterval"
                            sx={{...CARD_SX, pt: 0, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0, breakBefore: 'avoid'}}
                        >
                            <Divider sx={{mb: 0.5}}/>
                            <Box sx={settingsSectionSx}>
                                <AdmicityCronScheduler
                                    value={schoolInfo.syncPhotoInterval}
                                    onChange={(cron) => {
                                        setPhotoCronExpression(cron);
                                        if (onChange) {
                                            onChange(cron);
                                        }
                                    }}
                                    label="Photo Sync"
                                    // Maria, 20 October 2026: the name with an 'i', in the words of the General Settings video script
                                    title="Photo Sync"
                                    hover="Use the slider to bring student photos from SIMS into Admicity. Student photographs are not updated regularly, so we recommend a weekly photo sync between midnight and 5am."
                                />
                            </Box>
                        </Box>
                    );
                }
            }
        ], [schoolInfo.syncInterval, schoolInfo.syncPhotoInterval]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            sx={{
                mt: 1,
                width: {
                    xs: '100%',
                },
                maxWidth: editMode ? 'none' : 1200,
                '& > form': {columnCount: {xs: 1, lg: 2}, columnGap: '16px'},
                '& > form > h6:empty': {display: 'none'},
                mx: 'auto',
                position: 'relative',
            }}
        >
            {/* Maria, 20 October 2026: the tab name already says it, so the heading is only on the set-up screen.
                Maria, 28 October 2026: there is no General Settings any more, so the heading is School Setup */}
            {!editMode && <Typography sx={{marginBottom: 2}} variant="h6" fontWeight="bold">School Setup</Typography>}
            {
                showAlert
                    ? <Alert
                        sx={{marginBottom: 2}}
                        severity="info"
                        onClose={() => setShowAlert(false)}
                    >
                        Changes to the &quot;Hide House Groups&quot; or &quot;Hide Border Groups&quot; groups will take
                        effect during the next
                        synchronization, scheduled to occur in 1 hour.
                    </Alert>
                    : ''
            }
            <AdmicityForm
                textFields={textFields}
                // Maria, 20 October 2026: the fields in rows across the page, so there is less scrolling
                // Maria, 20 October 2026: the fields in rows across the page, so there is less scrolling
                fieldsGrid={{
                    // the boxes side by side only from 1200 pixels wide; at 1024 the reminder boxes were cut short
                    // AD-2741 (Maria, 20 October 2026): below 1200 the four reminder boxes stay on one row, two to a row on a phone
                    sx: {gridTemplateColumns: {xs: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(24, 1fr)'}, ...CARD_SX, columnSpan: 'all', pt: 1},
                    // Maria, 20 October 2026: smaller boxes, a heading above the reminders and the words on hover
                    size: 'small',
                    headings: {
                        noAccountAutoReminderInterval: (
                            // Maria, 20 October 2026: one heading, with the icon itself in it
                            // AD-2741: on a narrow screen the heading wraps like ordinary text, with the icon in the sentence
                            <Box component="span" sx={{display: {xs: 'inline', lg: 'inline-flex'}, alignItems: 'center', gap: 0.5}}>
                                Auto Reminders: hover over{' '}
                                {/* AD-2784: the icon in the heading has its own hover, so it does not look broken */}
                                <Tooltip title="Hover over the i in each reminder box below to read what that reminder does." arrow placement="top" componentsProps={HOVER_BOX}>
                                    <InfoOutlinedIcon fontSize="small" sx={{color: '#0A8572', verticalAlign: 'text-bottom', cursor: 'help'}} aria-label="i icon" data-testid="hover-autoReminders"/>
                                </Tooltip>
                                {' '}for information about each reminder
                            </Box>
                        )
                    },
                    // Maria's words from the General Settings video script; the limit of three reminders is from
                    // ApplicationOptions.MaxReminderCount, used by SendRemindToRegisterAccountCommandHandler
                    // Maria, 20 October 2026: 'Notes' and then an 'i'
                    labelHovers: {
                        notes: "The Notes field is optional. It can be used to record internal notes about your school's Admicity configuration."
                    },
                    // the unit shown inside each reminder box, as the heading no longer says hours
                    units: {
                        noAccountAutoReminderInterval: 'hours',
                        unconfirmedApplicationReminderIntervalInHours: 'hours',
                        unconfirmedStudentReminderIntervalInHours: 'hours',
                        medicineExpiryWarningDays: 'days'
                    },
                    hovers: {
                        // read from SendMedicineWarningsCommandHandler: the school's notification addresses and parents
                        // with an Admicity account are emailed; the number cannot be below 1
                        medicineExpiryWarningDays: 'Medicine Expiry is linked to the Medicine at School area of the Student Profile. A notification email is sent to the email addresses for profile change notifications, and to parents with an Admicity account, this many days before a medicine held by the school is due to expire. The default is 28 days. You can change this setting if you want to.',
                        emailAddresses: "You can specify one or more email addresses to receive notifications of user profile changes. Enter a comma between each email address if you need to add more than one. We recommend that schools create a new email account specifically for Admicity notifications. Mailbox rules can then be applied to this email account to direct each profile update to the right member of staff. For example, a profile update containing the word 'medical' would be sent to the school nurse.",
                        enquiryResponseEmail: 'If your school uses SIMS for Independent Schools and has an Admicity Enquiry form, you can enter an email address here to receive notifications each time an enquiry is submitted.',
                        noAccountAutoReminderInterval: 'Auto Reminder for Account Creation specifies how often, in hours, reminders are sent to parents who have not activated their Admicity account creation invitation. Up to three reminders are sent while the invitation is valid. To disable automatic reminders, set this value to 0.',
                        unconfirmedApplicationReminderIntervalInHours: "Auto Reminder for Applicant Profile determines how often, in hours, parents of Accepted applicants are reminded that they need to complete and confirm the outstanding sections of their child's Student Profile. A value of 0 disables the reminders.",
                        unconfirmedStudentReminderIntervalInHours: "Auto Reminder for Student Profile determines how often, in hours, parents of students on roll are reminded that they need to complete and confirm the outstanding sections of their child's Student Profile. A value of 0 disables the reminders."
                    },
                    span: {
                        // Maria, 20 October 2026: the school's name and the emails box shorter, with the logo beside them
                        name: {xs: '1 / -1', lg: 'span 9'},
                        dfeNumber: {xs: '1 / -1', lg: 'span 4'},
                        domain: {xs: '1 / -1', lg: 'span 8'},
                        emailAddresses: {xs: '1 / -1', lg: 'span 9'},
                        enquiryResponseEmail: {xs: '1 / -1', lg: 'span 12'},
                        // Maria, 20 October 2026: the three reminder boxes much smaller, with Notes to the right of them
                        // Maria, 20 October 2026: the four reminder boxes small, in the left half, under the emails box
                        noAccountAutoReminderInterval: REMINDER_SPAN,
                        unconfirmedApplicationReminderIntervalInHours: REMINDER_SPAN,
                        unconfirmedStudentReminderIntervalInHours: REMINDER_SPAN,
                        medicineExpiryWarningDays: REMINDER_SPAN,
                        notes: {xs: '1 / -1', lg: 'span 12'}
                    },
                    // Maria, 20 October 2026: Notes from the top of the reminders heading to the bottom of the reminder boxes
                    inGrid: ['logo', 'schoolAddress'],
                    // AD-2228 (Maria, 22 October 2026): the school's address under School Name, the emails under it, and
                    // the rows below one lower; the logo stays beside the three boxes on the left
                    place: {
                        logo: {gridColumn: {xs: '1 / -1', lg: '10 / 13'}, gridRow: {lg: '1 / 4'}},
                        schoolAddress: {gridColumn: {xs: '1 / -1', lg: '1 / 10'}, gridRow: {lg: 2}},
                        emailAddresses: {gridRow: {lg: 3}},
                        enquiryResponseEmail: {gridColumn: {xs: '1 / -1', lg: '13 / 25'}, gridRow: {lg: 2}},
                        'heading-noAccountAutoReminderInterval': {gridColumn: {xs: '1 / -1', lg: '1 / 13'}, gridRow: {lg: 4}, whiteSpace: {lg: 'nowrap'}},
                        noAccountAutoReminderInterval: {gridRow: {lg: 5}, ...FOUR_REMINDERS_TO_A_ROW},
                        unconfirmedApplicationReminderIntervalInHours: {gridRow: {lg: 5}, ...FOUR_REMINDERS_TO_A_ROW},
                        unconfirmedStudentReminderIntervalInHours: {gridRow: {lg: 5}, ...FOUR_REMINDERS_TO_A_ROW},
                        medicineExpiryWarningDays: {gridRow: {lg: 5}, ...FOUR_REMINDERS_TO_A_ROW},
                        notes: {
                            gridColumn: {xs: '1 / -1', lg: '13 / 25'},
                            gridRow: {lg: '3 / 6'},
                            display: 'flex',
                            '& .MuiFormControl-root': {mt: 1, mb: 0.5, flex: 1},
                            '& .MuiInputBase-root': {height: {lg: '100%'}, alignItems: 'flex-start'}
                        }
                    }
                }}
                custom={[
                    schoolAddressField,
                    toggleSections[0],
                    toggleSections[1],
                    toggleSections[2],
                    toggleSections[3],
                    toggleSections[4],
                    ...(user.hasRole(ROLES.SCHOOL_ADMIN) ? customFields : []),
                    ...syncFields
                ]}
                handleSubmit={handleSubmit}
                buttonText="Save"
                setIsChildFormValid={setIsFormValid}
                actionsContainerProps={{
                    container: {
                        justifyContent: 'flex-end',
                        sx: {columnSpan: 'all'}
                    },
                    button: {
                        fullWidth: false,
                        disabled: isLoading || !isFormValid
                    }
                }}
            />
            <AdmicityDialog
                open={confirmOpen}
                handleClose={handleCancelSave}
                title="Confirm Save"
                contentText="Are you sure you want to save these school setting changes?"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleCancelSave
                    },
                    {
                        label: 'Save',
                        onClick: handleConfirmSave
                    }
                ]}
            >
                {pendingInvitationWarning && (
                    <DialogContentText sx={{mt: 2, mb: 0}}>
                        Saving these account registration invitation changes will send invitations to all users and contacts who are eligible to receive them
                        {pendingEnabledInvitationLabels.length > 0 && (
                            <>
                                {': '}
                                {pendingEnabledInvitationLabels.map((label, index) => (
                                    <Box
                                        key={label}
                                        component="span"
                                        sx={{fontWeight: 700}}
                                    >
                                        {index > 0 ? ', ' : ''}
                                        {label}
                                    </Box>
                                ))}
                                .
                            </>
                        )}
                    </DialogContentText>
                )}
            </AdmicityDialog>
        </Box>
    );
};

SchoolSetupForm.defaultProps = {
    editMode: false
};

SchoolSetupForm.propTypes = {
    editMode: PropTypes.bool,
    schoolInfo: PropTypes.object,
    logo: PropTypes.object,
    onSubmit: PropTypes.func,
    isLoading: PropTypes.bool
};

export default SchoolSetupForm;
